// filepath: apps/web/src/features/admin/recordPermissions/RecordRuleEditorDialog.tsx
//
// FR-UI-ADM-2 AC-2 — ownership + condition rule editor dialog.
//
// Composes the FR-UI-ADM-3 `FormulaEditor` widget for both the
// ownership rule (record.owner == <user>) and the condition rule
// (expr-lang predicate).
//
// Live preview: fetches a bounded sample (POST /api/entities/{type}/query
// with limit:50) and calls POST /api/formula/evaluate once per
// record; renders "Matches X of 50 sampled records (preview)" so
// the wording honestly reflects the sampling.
//
// All text renders as JSX text nodes (INV-9).

import React, { useEffect, useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { FormulaEditor } from "@/admin/formula/FormulaEditor";
import { ApiError } from "@/definitions/types";
import { apiFetch } from "@/api";
import {
  useFieldPermissionsQuery,
  useRecordRuleMutation,
  useRecordRuleQuery,
} from "./queries";

interface RecordRuleEditorDialogProps {
  open: boolean;
  roleName: string;
  entityType: string;
  onClose: () => void;
  onSaved: () => void;
  onConflict: (serverVersion: number) => void;
}

const SAMPLE_LIMIT = 50;

interface PreviewState {
  status: "idle" | "loading" | "ready" | "error";
  matches: number;
  total: number;
  error: string | null;
}

const INITIAL_PREVIEW: PreviewState = {
  status: "idle",
  matches: 0,
  total: 0,
  error: null,
};

/**
 * fetchSample — POST /api/entities/{type}/query with limit:50
 * to get a bounded record sample for the preview.
 */
async function fetchSample(
  entityType: string,
): Promise<Array<Record<string, unknown>>> {
  const r = await apiFetch(
    `/api/entities/${encodeURIComponent(entityType)}/query`,
    {
      method: "POST",
      init: {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: SAMPLE_LIMIT }),
      },
    },
  );
  if (!r.ok) {
    throw new ApiError(r.statusText, r.status, `http_${r.status}`);
  }
  const body = (await r.json()) as { records?: Array<Record<string, unknown>> };
  return body.records ?? [];
}

/**
 * evaluateExpression — POST /api/formula/evaluate with a single
 * expression + context map. Returns the boolean result.
 */
async function evaluateExpression(
  expr: string,
  record: Record<string, unknown>,
): Promise<boolean> {
  const r = await apiFetch(`/api/formula/evaluate`, {
    method: "POST",
    init: {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        expressions: [expr],
        context: {
          record,
          user: {},
          tenant: {},
          now: new Date().toISOString(),
          env: { kind: "staging" },
        },
      }),
    },
  });
  if (!r.ok) {
    throw new ApiError(r.statusText, r.status, `http_${r.status}`);
  }
  const body = (await r.json()) as {
    results?: Array<{ value?: unknown }>;
  };
  const result = body.results?.[0]?.value;
  return Boolean(result);
}

export function RecordRuleEditorDialog({
  open,
  roleName,
  entityType,
  onClose,
  onSaved,
  onConflict,
}: RecordRuleEditorDialogProps): React.ReactElement | null {
  const { t } = useTranslation();
  const ruleQuery = useRecordRuleQuery(roleName, entityType);
  // The role's optimistic-concurrency token (INV-7) lives on the
  // FieldPermissionsReadResponse — see FR-AUTH-6 contract. The
  // RecordRuleResponse itself doesn't carry a version because the
  // server bumps the role's version on every rule write.
  const roleVersionQuery = useFieldPermissionsQuery(roleName, entityType);
  const mutation = useRecordRuleMutation(roleName, entityType);

  const [ownershipExpr, setOwnershipExpr] = useState("");
  const [conditionExpr, setConditionExpr] = useState("");
  const [version, setVersion] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<"ownership" | "condition">(
    "ownership",
  );
  const [preview, setPreview] = useState<PreviewState>(INITIAL_PREVIEW);

  // Hydrate local state from the server response on first open +
  // every successful refetch.
  useEffect(() => {
    if (open && ruleQuery.data) {
      setOwnershipExpr(ruleQuery.data.ownership_rule ?? "");
      setConditionExpr(ruleQuery.data.condition_rule ?? "");
      // Hydrate the role's optimistic-concurrency version from
      // the field-permissions read response (INV-7). If the
      // field-permissions query is still loading or has no data
      // yet, fall back to 1 — the very first PUT for a brand-new
      // role will succeed because version=1 is the column DEFAULT.
      setVersion(roleVersionQuery.data?.version ?? 1);
      setPreview(INITIAL_PREVIEW);
    }
  }, [open, ruleQuery.data, roleVersionQuery.data?.version]);

  if (!open) return null;

  const activeExpr = activeTab === "ownership" ? ownershipExpr : conditionExpr;

  const onActiveExprChange = (next: string) => {
    if (activeTab === "ownership") setOwnershipExpr(next);
    else setConditionExpr(next);
  };

  const runPreview = async () => {
    if (activeExpr.trim() === "") {
      setPreview(INITIAL_PREVIEW);
      return;
    }
    setPreview({ status: "loading", matches: 0, total: 0, error: null });
    try {
      const sample = await fetchSample(entityType);
      let matches = 0;
      for (const record of sample) {
        const ok = await evaluateExpression(activeExpr, record);
        if (ok) matches += 1;
      }
      setPreview({
        status: "ready",
        matches,
        total: sample.length,
        error: null,
      });
    } catch (err) {
      setPreview({
        status: "error",
        matches: 0,
        total: 0,
        error: err instanceof Error ? err.message : String(err),
      });
    }
  };

  const onSave = async () => {
    try {
      await mutation.mutateAsync({
        rule_kind: activeTab,
        rule_expr: activeExpr,
        version,
      });
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        try {
          const body = JSON.parse(err.message) as {
            current_version?: number;
          };
          if (body.current_version !== undefined) {
            onConflict(body.current_version);
          }
        } catch {
          // swallow — non-JSON error message
        }
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="record-rule-editor-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="record-rule-editor-dialog"
    >
      <div className="w-full max-w-2xl rounded-md border border-border bg-background p-4 shadow-lg">
        <h2 id="record-rule-editor-title" className="text-sm font-semibold">
          {t("admin.record_permissions.editor.title")}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {roleName} / {entityType}
        </p>
        <div className="mt-3 flex gap-2 border-b border-border">
          <button
            type="button"
            className={`px-3 py-1 text-xs ${activeTab === "ownership" ? "border-b-2 border-primary font-medium" : ""}`}
            onClick={() => setActiveTab("ownership")}
            data-testid="record-rule-tab-ownership"
          >
            {t("admin.record_permissions.editor.tab.ownership")}
          </button>
          <button
            type="button"
            className={`px-3 py-1 text-xs ${activeTab === "condition" ? "border-b-2 border-primary font-medium" : ""}`}
            onClick={() => setActiveTab("condition")}
            data-testid="record-rule-tab-condition"
          >
            {t("admin.record_permissions.editor.tab.condition")}
          </button>
        </div>
        <div className="mt-3">
          <FormulaEditor
            value={activeExpr}
            onChange={onActiveExprChange}
            fieldPath={`record-rule-${activeTab}-${roleName}-${entityType}`}
            labelKey={t("admin.record_permissions.editor.expr_label")}
            debounceMs={300}
          />
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs">
          <button
            type="button"
            className="rounded border border-border px-3 py-1"
            onClick={runPreview}
            data-testid="record-rule-preview"
          >
            {t("admin.record_permissions.editor.preview_button")}
          </button>
          {preview.status === "loading" && (
            <span className="text-muted-foreground">
              {t("admin.record_permissions.editor.preview.loading")}
            </span>
          )}
          {preview.status === "ready" && (
            <span
              className="text-foreground"
              data-testid="record-rule-preview-count"
            >
              {t(
                "admin.record_permissions.editor.preview.matches",
                "Matches {{matches}} of {{total}} sampled records (preview)",
                { matches: preview.matches, total: preview.total },
              )}
            </span>
          )}
          {preview.status === "error" && (
            <span className="text-destructive">{preview.error}</span>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-border px-3 py-1 text-xs"
            onClick={onClose}
            data-testid="record-rule-close"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className="rounded bg-primary px-3 py-1 text-xs text-primary-foreground disabled:opacity-50"
            onClick={onSave}
            disabled={mutation.isPending || activeExpr.trim() === ""}
            data-testid="record-rule-save"
          >
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
